import React from 'react';
import PageLayout from '../components/PageLayout';
import SEOHead from '../components/SEOHead';

const assessmentTypes = [
  {
    icon: '/pictures/shield_check_7246d46e1b.svg',
    title: 'Vulnerability Assessment',
    desc: 'Systematic review of security weaknesses in your information systems. We evaluate if the system is susceptible to known vulnerabilities, assign severity levels, and recommend remediation.',
  },
  {
    icon: '/pictures/security_80f8ac1422.svg',
    title: 'Penetration Testing',
    desc: 'Simulated cyberattack against your systems to check for exploitable vulnerabilities. Our ethical hackers think like attackers to find weaknesses before real hackers do.',
  },
  {
    icon: '/pictures/intrusion_detection_systems_82cce9bc0b.svg',
    title: 'Security Audit',
    desc: 'Comprehensive review of your organization\'s security policies, procedures, and controls. We identify gaps and provide a prioritized roadmap to improve your security posture.',
  },
  {
    icon: '/pictures/access_controls_4aabd4e0ee.svg',
    title: 'Compliance Assessment',
    desc: 'Evaluate your adherence to industry standards and regulations (ISO 27001, SOC 2, GDPR, PCI-DSS). We help you meet requirements and avoid costly penalties.',
  },
  {
    icon: '/pictures/email_security_601ebfc2ed.svg',
    title: 'Social Engineering Testing',
    desc: 'Phishing simulations, vishing, and physical security tests to measure your human attack surface. We train employees to become your strongest security asset.',
  },
  {
    icon: '/pictures/firewalls_21293cd107.svg',
    title: 'Red Team Exercise',
    desc: 'Full-scope, multi-layered attack simulation that tests people, processes, and technology simultaneously. The most realistic way to understand your real-world security posture.',
  },
];

const process = [
  { num: '01', title: 'Scoping', desc: 'Define assessment boundaries, objectives, and rules of engagement.' },
  { num: '02', title: 'Reconnaissance', desc: 'Gather intelligence about your systems, infrastructure, and personnel.' },
  { num: '03', title: 'Assessment', desc: 'Execute technical tests, audits, and evaluations across defined scope.' },
  { num: '04', title: 'Reporting', desc: 'Deliver detailed findings with risk ratings, evidence, and remediation guidance.' },
  { num: '05', title: 'Remediation Support', desc: 'Assist your team in addressing identified vulnerabilities and re-testing fixes.' },
];

export default function Cybersecurity() {
  return (
    <PageLayout>
      <SEOHead
        title="Cybersecurity Assessment Services"
        description="Comprehensive cybersecurity assessments including penetration testing, vulnerability assessments, red team operations, and compliance audits to protect your business."
        keywords="cybersecurity, penetration testing, security assessment, vulnerability assessment, red team, compliance audit, security testing"
        url="/services/cybersecurity"
      />
      <style>{`
        @media (max-width: 768px) {
          .cyber-intro-grid,
          .cyber-assessments-grid,
          .cyber-process-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      {/* Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Services</span>
            <span style={{ color: '#D1D5DB' }}>/</span>
            <span style={{ fontSize: '14px', color: '#0099CC', fontWeight: '500' }}>Cybersecurity Assessment</span>
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            Cybersecurity Assessment
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '640px', lineHeight: 1.7 }}>
            Comprehensive security audits, penetration testing, and vulnerability assessments to protect your business from modern cyber threats.
          </p>
        </div>
      </div>

      {/* Intro */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div className="cyber-intro-grid" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '24px', letterSpacing: '-0.5px' }}>Why Cybersecurity Assessment Matters</h2>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8, marginBottom: '20px' }}>
              The average cost of a data breach reached $4.88 million in 2024. Most breaches exploit known, unpatched vulnerabilities that a proper assessment would have identified months earlier.
            </p>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8 }}>
              DISION Tech\'s cybersecurity assessment services give you an attacker\'s perspective on your defenses — so you can fix vulnerabilities before they become breaches. Our team brings together offensive security expertise, deep compliance knowledge, and practical remediation experience.
            </p>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/net_Sec_ed720c1d33.png" alt="Cybersecurity" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* Assessment types */}
      <section style={{ padding: '80px 24px', background: '#F9FAFB' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Our Assessment Services</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '500px' }}>A full spectrum of security testing and assessment capabilities.</p>
          <div className="cyber-assessments-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {assessmentTypes.map((a, i) => (
              <div key={i} className="service-card" style={{ background: 'white', borderRadius: '16px', padding: '28px', border: '1px solid #E5E7EB' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px', padding: '11px' }}>
                  <img src={a.icon} alt={a.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#111827', marginBottom: '10px' }}>{a.title}</h3>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Our Assessment Process</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '480px' }}>A structured, repeatable approach that delivers consistent, high-quality results.</p>
          <div className="cyber-process-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
            {process.map((step, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '24px 16px', borderRadius: '16px', border: '1px solid #E5E7EB', background: '#F9FAFB' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#0099CC', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <span style={{ color: 'white', fontWeight: '700', fontSize: '13px' }}>{step.num}</span>
                </div>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>{step.title}</h3>
                <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
